import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Store, ChevronRight } from 'lucide-react';
import { brandService, type Brand } from '../services/brandService';

const API_URL = import.meta.env.VITE_API_URL ?? '';

function getLogoUrl(logoUrl: string): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://') || logoUrl.startsWith('blob:')) {
        return logoUrl;
    }
    const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    const path = logoUrl.startsWith('/') ? logoUrl : `/${logoUrl}`;
    return `${base}${path}`;
}

export default function Explore() {
    const navigate = useNavigate();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const data = await brandService.getAll();
                setBrands(data);
            } catch (err) {
                console.error('Error fetching brands:', err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchBrands();
    }, []);

    const filteredBrands = useMemo(() => {
        if (!searchTerm.trim()) return brands;
        const term = searchTerm.toLowerCase();
        return brands.filter(
            (b) =>
                b.name.toLowerCase().includes(term) ||
                b.category?.toLowerCase().includes(term)
        );
    }, [brands, searchTerm]);

    return (
        <div className="flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white/95 px-5 pb-3 pt-12 backdrop-blur-sm">
                <h1 className="text-2xl font-bold text-gray-900">Descubrir</h1>
            </div>

            <div className="flex flex-col gap-4 px-5 pb-6">
                {/* Search */}
                <div className="relative">
                    <Search
                        size={18}
                        className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Buscar comercios..."
                        className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    />
                </div>

                {/* Brand Cards */}
                {isLoading ? (
                    <div className="flex flex-col gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="animate-pulse overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm"
                            >
                                <div className="flex gap-4 p-4">
                                    <div className="h-14 w-14 shrink-0 rounded-xl bg-gray-200" />
                                    <div className="flex-1">
                                        <div className="mb-2 h-3 w-16 rounded bg-gray-200" />
                                        <div className="mb-2 h-4 w-36 rounded bg-gray-200" />
                                        <div className="h-3 w-full rounded bg-gray-100" />
                                    </div>
                                </div>
                                <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-4 py-3">
                                    <div className="h-4 w-28 rounded bg-gray-200" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : filteredBrands.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-8 text-center">
                        <Store size={40} className="mx-auto mb-3 text-gray-300" />
                        <p className="text-sm text-gray-400">
                            {searchTerm
                                ? `No se encontraron comercios para "${searchTerm}".`
                                : 'No hay comercios disponibles.'}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {filteredBrands.map((brand) => (
                            <div
                                key={brand.id}
                                className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-all hover:shadow-md"
                            >
                                {/* Card body */}
                                <div className="flex gap-4 p-4">
                                    {/* Logo */}
                                    {brand.logoUrl ? (
                                        <img
                                            src={getLogoUrl(brand.logoUrl)}
                                            alt={brand.name}
                                            className="h-14 w-14 shrink-0 rounded-xl border border-gray-100 object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-orange-50">
                                            <Store size={24} className="text-orange-400" />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold uppercase text-indigo-600">
                                            {brand.category || 'COMERCIO'}
                                        </p>
                                        <p className="font-bold text-lg text-gray-900 truncate">
                                            {brand.name}
                                        </p>
                                        <p className="text-sm text-gray-500 line-clamp-2">
                                            {brand.description ||
                                                'Acumula puntos y canjea recompensas exclusivas.'}
                                        </p>
                                    </div>
                                </div>

                                {/* Card footer */}
                                <div className="flex justify-end border-t border-gray-100 bg-gray-50 px-4 py-3">
                                    <button
                                        onClick={() => navigate(`/wallet/${brand.id}`)}
                                        className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-indigo-600 transition-colors hover:text-indigo-700"
                                    >
                                        Ver recompensas
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
